import clsx from "clsx";
import {
  createContext,
  FC,
  PropsWithChildren,
  Suspense,
  useContext,
  useEffect,
  useId,
  useMemo,
} from "react";
import { useFragment, useRelayEnvironment } from "react-relay";
import { Tooltip } from "react-tooltip";
import { graphql, readInlineData, requestSubscription } from "relay-runtime";
import { Asset, Balance, BalanceAccount, THOR } from "rujira.js";
import { OmniBalance, Provider, useTranslation } from "rujira.ui";
import {} from "../../portfolio/utils";
import { usePreloadedAccountData } from "../../services/accountData";
import { useAccounts } from "../../services/accounts";
import { balanceSubscriptionAddresses } from "../../services/balanceAddresses";
import { GET_STARTED_URL } from "../../home/constants";
import {
  BalanceContextFragment$data,
  BalanceContextFragment$key,
} from "./__generated__/BalanceContextFragment.graphql";
import {
  BalanceV2AccountFragment$data,
  BalanceV2AccountFragment$key,
} from "./__generated__/BalanceV2AccountFragment.graphql";
import { useRedactedV2 } from "../../redacted/v2";
import { displayedReserve, type PendingMove } from "../../redacted/v2/rotation";
import { useRedacted } from "../../services/redacted";
import { excludeMetadataAccount } from "../../services/privateAccount";

const accountFragment = graphql`
  fragment BalanceV2AccountFragment on BalanceV2Account @inline {
    address
    chain
    balance
    valueUsd
    asset {
      asset
      type
      chain
      price {
        current
      }
      metadata {
        symbol
        decimals
      }
      variants {
        native {
          denom
        }
      }
    }
  }
`;

const readAccount = (
  ref: BalanceV2AccountFragment$key
): BalanceV2AccountFragment$data => readInlineData(accountFragment, ref);

const fragment = graphql`
  fragment BalanceContextFragment on Account {
    balancesV2(first: 100, addresses: $addresses)
      @catch
      @connection(key: "BalanceContext_balancesV2") {
      __id
      edges {
        node {
          id
          balance
          asset {
            id
            asset
            type
            chain
            metadata {
              symbol
              decimals
            }
            price {
              current
              changeDay
            }
            variants {
              native {
                denom
              }
            }
          }
          valueUsd
          accounts {
            ...BalanceV2AccountFragment
          }
        }
      }
    }
  }
`;

const subscription = graphql`
  subscription BalanceContextSubscription(
    $connection: ID!
    $addresses: [Address!]!
  ) {
    balances(addresses: $addresses) @appendEdge(connections: [$connection]) {
      cursor
      node {
        ... on BalanceV2 {
          id
          balance
          asset {
            id
            asset
            type
            chain
            metadata {
              symbol
              decimals
            }
            price {
              current
              changeDay
            }
          }
          valueUsd
          accounts {
            ...BalanceV2AccountFragment
          }
        }
      }
    }
  }
`;

export const BalanceSubscriptionProvider: FC<PropsWithChildren> = ({
  children,
}) => (
  <>
    <Suspense fallback={null}>
      <BalanceSubscription />
    </Suspense>
    {children}
  </>
);

// This provider sits above the page's Suspense boundary. Relay can suspend both
// its account/order fragments and its balance fragment when unlocking or changing
// accounts. Keep that background work separate so a sync refresh neither reaches
// the root error boundary nor replaces the session, open modal or pending action.
const BalanceSubscription: FC = () => {
  const { isPrivateMode } = useRedacted();
  const { accountData } = usePreloadedAccountData();
  const { selected, accounts } = useAccounts();
  const data = useFragment<BalanceContextFragment$key>(fragment, accountData);
  // We need to filter the non-selected thorchain addresses; BalanceV2 nodes are Keyed
  // on concatenated addresses, so if extra thor1 addresses are added then we won't get updated
  const config = useMemo(() => {
    if (isPrivateMode) return null;
    const addresses = balanceSubscriptionAddresses(accounts, selected);
    if (!data?.balancesV2.ok) return null;
    return {
      subscription,
      variables: { addresses, connection: data?.balancesV2?.value.__id },
    };
  }, [selected, accounts, data, isPrivateMode]);
  const env = useRelayEnvironment();
  useEffect(() => {
    if (!config?.variables.connection) return;
    const sub = requestSubscription(env, config);
    return () => {
      sub.dispose();
    };
  }, [config]);

  return null;
};

const context = createContext<BalanceResult>({
  asset: undefined as unknown as Asset,
});

type NodeRaw = NonNullable<
  NonNullable<
    NonNullable<
      Extract<
        BalanceContextFragment$data["balancesV2"],
        { ok: true }
      >["value"]["edges"]
    >[number]
  >["node"]
>;

type Node = Omit<NodeRaw, "accounts"> & {
  accounts: readonly BalanceV2AccountFragment$data[];
};

type BalanceProviderProps = {
  asset: Asset;
  additionalBalance?: bigint;
};

/**
 * @asset The asset details for balances.
 * @additionalBalance Used to increase the balance displayed for the given asset on the
 * Rujira network. This is useful when providing a balance on a UI that allows a user
 * to maintain an existing position, and you want to allow them to max out that position
 * using the Assets in their wallet plus the amount already in contract for that position.
 */
export const BalanceProvider: FC<PropsWithChildren<BalanceProviderProps>> = ({
  asset,
  additionalBalance,
  children,
}) => {
  const v2 = useRedactedV2();
  const { isPrivateMode, subWalletAddress } = useRedacted();
  const { accountData } = usePreloadedAccountData();
  const data = useFragment<BalanceContextFragment$key>(fragment, accountData);
  const walletBalance = findBalance(
    data,
    asset,
    v2.enabled ? v2.proxyContract : undefined
  );
  const metadataAssets = usePreloadedMetadataAssets();
  const denom = nativeDenomOf(asset, v2.reserveBalances.keys(), metadataAssets);
  const reserveAmount = denom ? v2.reserveBalances.get(denom) : undefined;
  const reserveAccount: BalanceAccount | undefined =
    v2.enabled && denom && reserveAmount !== undefined
      ? {
          address: `redacted-v2:${denom}`,
          chain: "STEALTH",
          asset,
          balance: reserveAmount,
          valueUsd: asset.price?.current
            ? (reserveAmount * asset.price.current) / 10n ** 12n
            : 0n,
        }
      : undefined;
  const reserveBalance: Balance | undefined = reserveAccount
    ? {
        asset,
        balance: reserveAccount.balance,
        valueUsd: reserveAccount.valueUsd,
        accounts: [reserveAccount],
      }
    : balanceForChain(walletBalance.balance, "STEALTH");
  const spendable = balanceForChain(walletBalance.balance, THOR);
  // Model B: one private wallet. Products see Reserve + the interaction's sub
  // as a single balance; execution funds the one-time sub automatically.
  const unified =
    v2.modelB.enabled && isPrivateMode
      ? unifiedPrivateBalance(
          resolveNativeAsset(asset, denom, metadataAssets),
          spendable,
          reserveAccount,
          subWalletAddress,
          v2.modelB.feeBps,
          v2.modelB.pendingMoves,
          denom
        )
      : undefined;
  const value = useMemo(
    () => ({
      ...walletBalance,
      ...(unified && { balance: unified.balance, account: unified.account }),
      subBalance: spendable,
      subAccount: spendable?.accounts[0],
      proxyBalance: reserveBalance,
      proxyAccount: reserveAccount,
      additionalBalance,
    }),
    [
      additionalBalance,
      reserveAccount,
      reserveBalance,
      spendable,
      unified,
      walletBalance,
    ]
  );
  return <context.Provider value={value}>{children}</context.Provider>;
};

/**
 * The asset a private message is built from must carry its native denom
 * (funds are named by it). Pages whose fragments omit `variants` get the
 * complete metadata asset, or at least the resolved denom.
 */
export const resolveNativeAsset = (
  asset: Asset,
  denom: string | undefined,
  metadata: readonly Asset[]
): Asset => {
  if (asset.variants?.native?.denom || !denom) return asset;
  const known = metadata.find((candidate) => candidate.asset === asset.asset);
  if (known?.variants?.native?.denom) return { ...asset, ...known };
  return {
    ...asset,
    variants: { ...asset.variants, native: { denom } },
  } as Asset;
};

/**
 * Native THORChain denom of an asset, even when a page's GraphQL fragment did
 * not load `variants.native` (e.g. trade pairs). Metadata first; the name-based
 * guess is only accepted when the Reserve actually holds that denom.
 */
export const nativeDenomOf = (
  asset: Asset,
  reserveDenoms: Iterable<string>,
  metadata: readonly Asset[]
): string | undefined => {
  const direct = asset.variants?.native?.denom;
  if (direct) return direct;
  const known = metadata.find((candidate) => candidate.asset === asset.asset)
    ?.variants?.native?.denom;
  if (known) return known;
  const held = new Set(reserveDenoms);
  const name = asset.asset.toLowerCase();
  const [chain, ...rest] = name.split(".");
  const symbol = rest.join(".");
  const candidates =
    chain === "thor" && symbol
      ? [`x/${symbol}`, symbol]
      : [name.replace(".", "-"), name];
  return candidates.find((candidate) => held.has(candidate));
};

/**
 * Reserve and the active sub as one spendable balance (Model B). The amount
 * is what an action can actually use: funding the one-time sub deducts the
 * protocol fee from the Reserve transfer, and the call adds it once more on
 * its amount, so MAX never asks for more than the Reserve can fund.
 */
export const unifiedPrivateBalance = (
  asset: Asset,
  spendable: Balance | undefined,
  reserve: BalanceAccount | undefined,
  subwallet: string | null,
  feeBps = 0,
  // Confirmed Reserve↔sub moves the published Reserve does not show yet:
  // without them a funded amount briefly counts twice.
  moves: readonly PendingMove[] = [],
  denom = reserve?.address.startsWith("redacted-v2:")
    ? reserve.address.slice("redacted-v2:".length)
    : undefined
): { balance?: Balance; account?: BalanceAccount } => {
  const shownSub = spendable?.balance ?? 0n;
  const reserveAmount = denom
    ? displayedReserve(denom, reserve?.balance ?? 0n, shownSub, moves)
    : (reserve?.balance ?? 0n);
  if (!spendable && reserveAmount <= 0n) return {};
  const bps = BigInt(Math.max(0, Math.min(1_000, Math.floor(feeBps))));
  const reserveNet = reserveAmount - (reserveAmount * bps + 9_999n) / 10_000n;
  const gross = shownSub + (reserveNet > 0n ? reserveNet : 0n);
  const amount = (gross * 10_000n) / (10_000n + bps);
  // Unit value from the raw sources; the shown amount may be adjusted.
  const rawAmount = shownSub + (reserve?.balance ?? 0n);
  const totalValue = (spendable?.valueUsd ?? 0n) + (reserve?.valueUsd ?? 0n);
  const valueUsd = rawAmount > 0n ? (totalValue * amount) / rawAmount : 0n;
  const base = spendable?.accounts[0];
  const account: BalanceAccount = {
    ...(base ?? { asset }),
    // Always the interaction's sub: message and execution must match.
    address: subwallet || base?.address || "",
    chain: THOR,
    asset: base?.asset ?? asset,
    balance: amount,
    valueUsd,
  };
  return {
    account,
    balance: { asset, balance: amount, valueUsd, accounts: [account] },
  };
};

export const usePreloadedBalance = () => {
  return useContext(context);
};

export const useActiveBalance = () => {
  const { isPrivateMode } = useRedacted();
  const v2 = useRedactedV2();
  const value = usePreloadedBalance();
  const unified = isPrivateMode && v2.modelB.enabled;
  return {
    ...value,
    balance: isPrivateMode && !unified ? value.subBalance : value.balance,
    account: isPrivateMode && !unified ? value.subAccount : value.account,
    isPrivate: isPrivateMode,
  };
};

export const usePreloadedBalanceData = (createSubscriptions = true) => {
  const v2 = useRedactedV2();
  const { accountData } = usePreloadedAccountData(createSubscriptions);
  const result = useFragment<BalanceContextFragment$key>(
    fragment,
    accountData
  )?.balancesV2;
  return useMemo(() => {
    if (!v2.enabled || !v2.proxyContract || !result?.ok) return result;
    const edges = result.value.edges?.flatMap((edge) => {
      if (!edge?.node) return edge === null ? [null] : [];
      const retainedRefs = edge.node.accounts.filter(
        (ref) => readAccount(ref).address !== v2.proxyContract
      );
      const filtered = excludeMetadataAccount(
        retainedRefs.map(readAccount),
        v2.proxyContract
      );
      const denom = edge.node.asset.variants?.native?.denom;
      if (
        filtered.accounts.length === 0 &&
        (!denom || !v2.reserveBalances.has(denom))
      ) {
        return [];
      }
      return [
        {
          ...edge,
          node: {
            ...edge.node,
            accounts: retainedRefs,
            balance: filtered.balance,
            valueUsd: filtered.valueUsd,
          },
        },
      ];
    });
    return { ...result, value: { ...result.value, edges } };
  }, [result, v2.enabled, v2.proxyContract, v2.reserveBalances]);
};

/** Asset metadata returned alongside the private account query. Proxy account
 * amounts are deliberately not exposed by this hook. */
export const usePreloadedMetadataAssets = (): Asset[] => {
  const { accountData } = usePreloadedAccountData(false);
  const result = useFragment<BalanceContextFragment$key>(
    fragment,
    accountData
  )?.balancesV2;
  return useMemo(() => {
    if (!result?.ok) return [];
    const byDenom = new Map<string, Asset>();
    for (const edge of result.value.edges ?? []) {
      const asset = edge?.node?.asset;
      const denom = asset?.variants?.native?.denom;
      if (asset && denom) byDenom.set(denom, asset);
    }
    return [...byDenom.values()].sort((a, b) =>
      a.metadata.symbol.localeCompare(b.metadata.symbol)
    );
  }, [result]);
};

/**
 * Balances as product pages see them. In Model B private mode this is the one
 * private wallet (Reserve + active one-time sub, net of fees), so every page
 * (swap, trade, borrow, stake, liquidate) shows what an action can spend.
 */
export const usePreloadedBalances = (createSubscriptions = true): Node[] => {
  const { isPrivateMode } = useRedacted();
  const v2 = useRedactedV2();
  const raw = useRawPreloadedBalances(createSubscriptions);
  const unified = useActiveBalances();
  return isPrivateMode && v2.modelB.enabled ? unified : raw;
};

/** Account-scoped GraphQL balances only (the active sub in private mode). */
const useRawPreloadedBalances = (createSubscriptions = true): Node[] => {
  const data = usePreloadedBalanceData(createSubscriptions);
  return useMemo(() => {
    if (!data?.ok) return [];
    return (
      data.value.edges?.reduce<Node[]>(
        (a, v) =>
          v?.node
            ? [{ ...v.node, accounts: v.node.accounts.map(readAccount) }, ...a]
            : a,
        []
      ) ?? []
    );
  }, [data]);
};

export const usePreloadedMyProxyBalances = (): Node[] => {
  const v2 = useRedactedV2();
  const metadata = usePreloadedMetadataAssets();
  const balances = useRawPreloadedBalances();
  return useMemo(() => {
    if (!v2.enabled)
      return balances.flatMap((node) => nodeForChain(node, "STEALTH"));
    return [...v2.reserveBalances.entries()].flatMap(([denom, amount]) => {
      const asset = metadata.find(
        (candidate) => candidate.variants?.native?.denom === denom
      );
      if (!asset) return [];
      const valueUsd = asset.price?.current
        ? (amount * asset.price.current) / 10n ** 12n
        : 0n;
      const account: BalanceAccount = {
        address: `redacted-v2:${denom}`,
        chain: "STEALTH",
        asset,
        balance: amount,
        valueUsd,
      };
      return [
        {
          id: `redacted-v2:${denom}`,
          asset,
          balance: amount,
          valueUsd,
          accounts: [account],
        } as unknown as Node,
      ];
    });
  }, [balances, metadata, v2.enabled, v2.reserveBalances]);
};

/** Balances held by the private spending account and available to Rujira apps. */
export const usePreloadedSubBalances = (): Node[] => {
  const balances = useRawPreloadedBalances();
  return useMemo(
    () => balances.flatMap((node) => nodeForChain(node, THOR)),
    [balances]
  );
};

/** Balances used by product actions in the currently selected account mode. */
export const useActiveBalances = (): Node[] => {
  const { isPrivateMode, subWalletAddress } = useRedacted();
  const v2 = useRedactedV2();
  const balances = useRawPreloadedBalances();
  const privateBalances = usePreloadedSubBalances();
  const reserve = usePreloadedMyProxyBalances();
  const unified = useMemo(
    () =>
      unifiedNodes(
        reserve,
        privateBalances,
        subWalletAddress,
        v2.modelB.feeBps,
        v2.modelB.pendingMoves
      ),
    [
      privateBalances,
      reserve,
      subWalletAddress,
      v2.modelB.feeBps,
      v2.modelB.pendingMoves,
    ]
  );
  if (!isPrivateMode) return balances;
  return v2.modelB.enabled ? unified : privateBalances;
};

/** Model B: one spendable node per asset (Reserve + active sub, net of fees). */
const unifiedNodes = (
  reserve: readonly Node[],
  sub: readonly Node[],
  subwallet: string | null,
  feeBps: number,
  moves: readonly PendingMove[]
): Node[] => {
  const assets = new Map<string, Node>();
  for (const node of [...reserve, ...sub])
    if (!assets.has(node.asset.asset)) assets.set(node.asset.asset, node);
  return [...assets.values()].flatMap((template) => {
    const own = sub.find((node) => node.asset.asset === template.asset.asset);
    const held = reserve.find(
      (node) => node.asset.asset === template.asset.asset
    );
    const { balance, account } = unifiedPrivateBalance(
      template.asset as Asset,
      own && {
        asset: own.asset as Asset,
        balance: own.balance,
        valueUsd: own.valueUsd,
        accounts: [...own.accounts],
      },
      held?.accounts[0],
      subwallet,
      feeBps,
      moves,
      template.asset.variants?.native?.denom ?? undefined
    );
    if (!balance || !account || balance.balance <= 0n) return [];
    return [
      {
        ...template,
        balance: balance.balance,
        valueUsd: balance.valueUsd,
        accounts: [account],
      } as unknown as Node,
    ];
  });
};

export type PrivateBalanceRow = {
  asset: Asset;
  amount: bigint;
  valueUsd: bigint;
};

/** One row per asset across Reserve and Spending, without exposing V2 notes. */
export const usePrivateBalanceRows = (): PrivateBalanceRow[] => {
  const reserve = usePreloadedMyProxyBalances();
  const spending = usePreloadedSubBalances();
  return useMemo(() => {
    const rows = new Map<string, PrivateBalanceRow>();
    for (const node of [...reserve, ...spending]) {
      const current = rows.get(node.asset.asset);
      rows.set(node.asset.asset, {
        asset: node.asset,
        amount: (current?.amount ?? 0n) + node.balance,
        valueUsd: (current?.valueUsd ?? 0n) + node.valueUsd,
      });
    }
    return [...rows.values()];
  }, [reserve, spending]);
};

export type UnifiedPrivateRow = PrivateBalanceRow & {
  /** Settled in a one-time sub, on its way back to the Reserve. */
  returning: bigint;
};

/** Model B wallet rows: Reserve notes plus balances still in any owned sub. */
export const useUnifiedPrivateRows = (): UnifiedPrivateRow[] => {
  const v2 = useRedactedV2();
  const reserve = usePreloadedMyProxyBalances();
  const metadata = usePreloadedMetadataAssets();
  return useMemo(() => {
    const assets = new Map<string, Asset>();
    const reserveBy = new Map<string, bigint>();
    const subBy = new Map<string, bigint>();
    const assetOf = (denom: string) =>
      assets.get(denom) ??
      metadata.find((candidate) => candidate.variants?.native?.denom === denom);
    for (const node of reserve) {
      const denom = node.asset.variants?.native?.denom;
      if (!denom) continue;
      assets.set(denom, node.asset);
      reserveBy.set(denom, (reserveBy.get(denom) ?? 0n) + node.balance);
    }
    for (const coins of v2.modelB.subBalances.values())
      for (const coin of coins) {
        const asset = assetOf(coin.denom);
        if (!asset) continue;
        assets.set(coin.denom, asset);
        subBy.set(
          coin.denom,
          (subBy.get(coin.denom) ?? 0n) + BigInt(coin.amount)
        );
      }
    for (const move of v2.modelB.pendingMoves) {
      const asset = assetOf(move.denom);
      if (asset) assets.set(move.denom, asset);
    }
    return [...assets].flatMap(([denom, asset]) => {
      const sub = subBy.get(denom) ?? 0n;
      const amount =
        displayedReserve(
          denom,
          reserveBy.get(denom) ?? 0n,
          sub,
          v2.modelB.pendingMoves
        ) + sub;
      if (amount <= 0n && !reserveBy.has(denom)) return [];
      return [
        {
          asset,
          amount,
          returning: sub,
          valueUsd: asset.price?.current
            ? (amount * asset.price.current) / 10n ** 12n
            : 0n,
        },
      ];
    });
  }, [metadata, reserve, v2.modelB.pendingMoves, v2.modelB.subBalances]);
};

export const usePreloadedAssetBalance = (asset: Asset): BalanceResult => {
  const v2 = useRedactedV2();
  const { isPrivateMode, subWalletAddress } = useRedacted();
  const { accountData } = usePreloadedAccountData(false);
  const data = useFragment<BalanceContextFragment$key>(fragment, accountData);
  const metadataAssets = usePreloadedMetadataAssets();
  const raw = findBalance(
    data,
    asset,
    v2.enabled ? v2.proxyContract : undefined
  );
  if (!isPrivateMode || !v2.modelB.enabled) return raw;
  // Model B: the same single private wallet the BalanceProvider exposes.
  const denom = nativeDenomOf(asset, v2.reserveBalances.keys(), metadataAssets);
  const amount = denom ? v2.reserveBalances.get(denom) : undefined;
  const reserve: BalanceAccount | undefined =
    denom && amount !== undefined
      ? {
          address: `redacted-v2:${denom}`,
          chain: "STEALTH",
          asset,
          balance: amount,
          valueUsd: asset.price?.current
            ? (amount * asset.price.current) / 10n ** 12n
            : 0n,
        }
      : undefined;
  const unified = unifiedPrivateBalance(
    resolveNativeAsset(asset, denom, metadataAssets),
    balanceForChain(raw.balance, THOR),
    reserve,
    subWalletAddress,
    v2.modelB.feeBps,
    v2.modelB.pendingMoves,
    denom
  );
  return unified.balance
    ? { asset, balance: unified.balance, account: unified.account }
    : { asset };
};

export type OmniFunding = {
  /** Held on Rujira, so an app-layer msg can spend it. */
  spendable: bigint;
  /** USD value held on other chains — what a deposit could still bring across. */
  depositableUsdValue: bigint;
};

/**
 * Split one asset's omnichain balance into what an app-layer msg can spend immediately
 * and what usd value is still sitting elsewhere.
 */
export const useOmniFunding = (asset: Asset): OmniFunding => {
  const { isPrivateMode } = useRedacted();
  const accounts = usePreloadedAssetBalance(asset).balance?.accounts ?? [];
  const onRujira = (a: BalanceAccount) =>
    a.asset.type === "SECURED" || a.asset.chain === THOR;
  // Model B: usePreloadedAssetBalance already includes the Reserve.

  return {
    spendable:
      accounts.find((account) =>
        isPrivateMode ? account.chain === THOR : onRujira(account)
      )?.balance ?? 0n,
    depositableUsdValue: accounts
      .filter((a) => !onRujira(a))
      .reduce((sum, a) => sum + a.valueUsd, 0n),
  };
};

export const BalanceCompact: FC<{
  className?: string;
  onClick?: (a: Balance | BalanceAccount) => void;
}> = ({ className, onClick }) => {
  const { t } = useTranslation("common");
  const { balance, additionalBalance, isPrivate } = useActiveBalance();
  const { accounts: publicAccounts } = useAccounts();
  const { subWalletAddress } = useRedacted();
  const accounts = isPrivate
    ? [
        {
          address: { address: subWalletAddress || "", networks: [THOR] },
          provider: "Keplr" as Provider.Key,
        },
      ]
    : publicAccounts;
  const tooltipId = useId();

  const balanceAdjusted = useMemo(() => {
    if (!balance) return undefined;
    if (!additionalBalance || additionalBalance === 0n) return balance;

    const adjustedAccounts = balance.accounts.map((account) => {
      if (account.asset.type === "SECURED") {
        const newBalanceAmount = account.balance + additionalBalance;
        return {
          ...account,
          balance: newBalanceAmount,
          valueUsd:
            account.balance === 0n
              ? 0n
              : BigInt(
                  Math.floor(
                    (Number(newBalanceAmount) / Number(account.balance)) *
                      Number(account.valueUsd)
                  )
                ),
        };
      } else {
        return account;
      }
    });

    return {
      ...balance,
      balance: (balance.balance ?? 0n) + (additionalBalance ?? 0n),
      accounts: adjustedAccounts,
    };
  }, [balance, additionalBalance]);

  return (
    <>
      <div
        className={clsx({
          "flex condensed fs-12 color-grey": true,
          [`${className}`]: className,
        })}>
        <div className="col">
          <span
            className="iflex ai-c pointer hover-white tooltip-label"
            data-tooltip-id={tooltipId}>
            {t("available")}
          </span>
        </div>
        <OmniBalance
          balance={balanceAdjusted}
          onClick={onClick}
          className="condensed fs-14 hover-white"
          accountProviders={accounts}
        />
      </div>
      <Tooltip id={tooltipId} className="tooltip" clickable delayHide={100}>
        <div className="w-30 text-left">
          {t("availableBalanceTooltipP1")}
          <br />
          <br />
          {t("availableBalanceTooltipP2")}
          <br />
          <br />
          <a
            href={`${GET_STARTED_URL}/omnichain`}
            className="color-primary1 hover-primary2 no-underline fw-500">
            {t("availableBalanceTooltipLink")}
          </a>
        </div>
      </Tooltip>
    </>
  );
};

export type BalanceResult = {
  asset: Asset;
  balance?: Balance;
  proxyBalance?: Balance;
  subBalance?: Balance;
  account?: BalanceAccount;
  proxyAccount?: BalanceAccount;
  subAccount?: BalanceAccount;
  additionalBalance?: bigint;
};

export const createBalanceProvider = <T extends string>(): [
  FC<
    PropsWithChildren<{
      assets: Record<T, Asset>;
    }>
  >,
  () => Record<T, BalanceResult>,
] => {
  const init = {} as Record<T, BalanceResult>;
  const context = createContext<Record<T, BalanceResult>>(init);
  const Provider: FC<PropsWithChildren<{ assets: Record<T, Asset> }>> = ({
    children,
    assets,
  }) => {
    const v2 = useRedactedV2();
    const { accountData } = usePreloadedAccountData();
    const data = useFragment<BalanceContextFragment$key>(fragment, accountData);
    const value = (Object.entries(assets) as [T, Asset][]).reduce(
      (a, [k, v]) => ({
        ...a,
        [k]: findBalance(data, v, v2.enabled ? v2.proxyContract : undefined),
      }),
      init
    );
    return <context.Provider value={value}>{children}</context.Provider>;
  };
  const useConsumer = () => useContext(context);
  return [Provider, useConsumer];
};

const findBalance = (
  data: BalanceContextFragment$data | null | undefined,
  asset: Asset,
  excludedAddress?: string
): BalanceResult => {
  if (!data?.balancesV2.ok) return { asset };
  const balanceNode = data?.balancesV2.value.edges?.find(
    (a) => a?.node?.asset?.asset === asset?.asset
  )?.node;
  if (balanceNode) {
    const filtered = excludeMetadataAccount(
      balanceNode.accounts.map(readAccount),
      excludedAddress
    );
    if (filtered.accounts.length === 0) return { asset };
    return {
      asset,
      balance: { ...balanceNode, ...filtered },
      account:
        filtered.accounts.find((x) => x.asset.asset === asset.asset) ||
        filtered.accounts[0],
    };
  }

  // We might explicitly need a balance for a non-unified token, eg WBTC. In that case construct a new Balance object
  const account = (data?.balancesV2.value.edges || [])
    .flatMap((a) => a?.node?.accounts.map(readAccount) || [])
    .find(
      (a) => a.address !== excludedAddress && a.asset.asset === asset.asset
    );
  if (account)
    return {
      asset,
      account,
      balance: {
        asset: account.asset,
        balance: account.balance,
        accounts: [account],
        valueUsd: account.valueUsd,
      },
    };
  return { asset };
};

const balanceForChain = (
  balance: Balance | undefined,
  chain: string
): Balance | undefined => {
  if (!balance) return undefined;
  const accounts = balance.accounts.filter(
    (account) => account.chain === chain
  );
  if (!accounts.length) return undefined;
  return {
    ...balance,
    accounts,
    balance: accounts.reduce((total, account) => total + account.balance, 0n),
    valueUsd: accounts.reduce((total, account) => total + account.valueUsd, 0n),
  };
};

const nodeForChain = (node: Node, chain: string): Node[] => {
  const balance = balanceForChain(
    {
      asset: node.asset,
      accounts: [...node.accounts],
      balance: node.balance,
      valueUsd: node.valueUsd,
    },
    chain
  );
  return balance
    ? [
        {
          ...node,
          accounts: balance.accounts,
          balance: balance.balance,
          valueUsd: balance.valueUsd,
        } as unknown as Node,
      ]
    : [];
};
